<template>
	<div v-if="shown">
		<div class="sidebar-micro-button-container" :class="{active: active, locked, warning, error}" @click="clickCallback">
			<div class="micro-button-icon" v-html="getIcon(iconName)"></div>
			<p class="title" :class="{active: active}">{{ title }}</p>
			<AppChevronIcon v-if="!!children && !locked" class="chevron-dropdown" :class="{invisible: active}" :direction="active ? 'up' : 'down'" />
			<div v-if="locked" class="locked-micro-icon" v-html="getIcon('iconLock')"></div>
		</div>
		<AppDropdown square-corner class="sidebar-micro-dropdown" v-if="!!children" :collapsed="!active" >
			<div v-for="(child, index) in children" :key="index">
				<SidebarMicroButton
					:ref="'micro-button-child-'+index"
					class="inner-micro-button"
					:title="child.name"
					:icon-name="child.icon"
					:shown="child.shown"
					:active="index === activeChild"
					:locked="child.locked"
					:warning="child.warning"
					:error="child.error"
					@button-click="(active) => clickChild(index, active)" />
			</div>
		</AppDropdown>
	</div>
</template>

<script>
import {useSvgStore} from "@/shared/stores";
import AppChevronIcon from "@/shared/components/AppChevronIcon.vue";
import AppDropdown from "@/shared/components/AppDropdown.vue";

export default {
	components: {AppDropdown, AppChevronIcon},
	setup() {
		const svgStore = useSvgStore();
		return {
			getIcon: svgStore.get,
		}
	},
	name: "SidebarMicroButton",
	emits: ["button-click", "child-button-click"],
	props: {
		active: {
			type: Boolean,
			default: false
		},
		locked: {
			type: Boolean,
			default: false
		},
		error: {
			type: Boolean,
			default: false
		},
		warning: {
			type: Boolean,
			default: false
		},
		title: {
			type: String,
			required: true
		},
		iconName: {
			type: String,
			required: true
		},
		shown: {
			type: Boolean,
			default: true
		},
		children: {
			type: Array,
			default: null,
			validator(value) {
				return value === null || value.every(item => item.name && item.icon && item.path);
			}
		},
		activeChild: {
			type: Number,
			default: 0
		}
	},
	methods: {
		clickCallback() {
			this.$emit("button-click", !this.active);
		},
		clickChild(index, active) {
			this.$emit("child-button-click", index, active);
		}
	}
}
</script>

<style lang="scss" scoped>
.sidebar-micro-button-container {
	display: flex;
	position: relative;
	cursor: pointer;
	pointer-events: all;
	align-items: center;
	justify-content: flex-start;
	width: 100%;
	height: 45px;
	transition: background-color 0.2s $transition, box-shadow 0.2s $transition;
	margin: 0 auto;
	padding: 0 15px;

	:after {
		content: "";
		position: absolute;
		top: 0;
		right: 0;
		width: 2px;
		height: 100%;
		background-color: transparent;
		transition: background-color 0.2s $transition;

	}

	&:hover {
		background-color: var(--color-fill-button-hover);
	}

	&:deep(svg) {
		.fill {
			fill: var(--color-txt);
		}
		.stroke {
			stroke: var(--color-txt);
		}
	}

	&.active {
		background-color: var(--color-active-button);

		&:not(.warning):not(.error)::after {
			background-color: var(--color-primary);
		}

		&:deep(svg) {
			.fill {
				fill: var(--color-active-button-text);
			}
			.stroke {
				stroke: var(--color-active-button-text);
			}
		}

	}
	&.warning {
		::after {
			background-color: #f2d60f;
		}
	}
	&.error {
		::after {
			background-color: #e83d15;
		}
	}

	.micro-button-icon {
		margin-right: 10px;
		width: 17px;
		height: 17px;

		:deep(svg) {
			width: 100%;
			height: 100%;
		}
	}

	p {
		font-size: 14px;
		margin-right: 8px;
		user-select: none;
	}

	.chevron-dropdown {
		margin-left: auto;
		transition: opacity 0.25s $transition 0.3s;
		opacity: 1;

		&.invisible {
			opacity: 0;
		}
	}

	.title {
		@include truncateString;
		max-width: calc(100% - 30px);

		&.active {
			color: var(--color-active-button-text);
		}
	}

	&.locked {
		>*:not(.locked-micro-icon) {
			opacity: .5;
		}

		.title {
			max-width: calc(100% - 46px);
		}
		.locked-micro-icon {
			@include centered;
			width: 10px;
			margin-left: auto;

			&:deep(svg) {
				height: 100%;
				width: 100%;
				.fill { fill: var(--color-txt); }
			}
		}
	}

}

.bg-emphasis {
	.sidebar-micro-button-container.active :after {
		display: none;
	}
}

.sidebar-micro-dropdown {

	:deep(.dropdown-content) {
		margin-top: 0 !important;
	}

	.inner-micro-button {
		margin: 3px 0;

		:deep(.sidebar-micro-button-container) {
			border-radius: 13px;
			transition: background-color 0.2s $transition, box-shadow 0.2s $transition;

			&:hover {
				background-color: var(--color-fill-button-hover);
			}

			&.active {
				background-color: var(--color-fill-button-hover);
				box-shadow: inset 0 2px 3px var(--color-box-shadow-3);

				p {
					color: var(--color-primary);
				}
				svg {
					.fill {
						fill: var(--color-primary);
					}
					.stroke {
						stroke: var(--color-primary);
					}
				}

				/*::after {
					display: none;
				}*/
			}
			&.warning {
				border: 1px solid #f2d60f;
				::after {
					display: none;
				}
			}
			&.error {
				border: 1px solid #e83d15;
				::after {
					display: none;
				}
			}

		}
	}

}

</style>